import { LoadingBlock } from "../UI/Loading/LoadingRows.tsx";
import { FullPageLoading } from "../UI/Loading/FullPageLoading.tsx";
import { Layout } from "../UI/Layout/Layout.tsx";
import { formatDateTime } from "../UI/Utils/formatDate.ts";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { useParams, useNavigate } from "react-router-dom";
import { useGetBulkClassRoomCreationIntent } from "./useGetBulkClassRoomCreationIntent.ts";
import { useCalculateBulkClassRoomCreationSlots } from "./useCalculateBulkClassRoomCreationSlots.ts";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { useMemo, useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { useTry1on1TeacherSubstitution } from "./useTry1on1TeacherSubstitution.ts";
import { SubstitutionResultModal } from "./SubstitutionResultModal.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { ErrorMessagePopup } from "../UI/MessagePopup/ErrorMessagePopup.tsx";
import { useMessagePopupController } from "../UI/MessagePopup/useMessagePopupController.ts";
import {
  ClassRoomType,
  TeacherSubstitutionResultGraphqlDto,
} from "../gql/graphql.ts";
import { useCreateBulk1on1ClassRooms } from "./useCreateBulk1on1ClassRooms.ts";
import { useDeleteBulkClassRoomCreationIntent } from "./useDeleteBulkClassRoomCreationIntent.ts";
import { useUpdateBulkClassRoomCreationIntent } from "./useUpdateBulkClassRoomCreationIntent.ts";
import { useAllFranchisees } from "../Franchisee/useAllFranchisees.ts";
import { WeekSlotInput } from "./WeekSlotInput.tsx";
import {
  DateTimeInput,
  Select,
  OutlinedButton,
  DangerButton,
} from "@normasteaching/norma-ui";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import {
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

export const BulkClassRoomIntentDetailScreenPath =
  "/bulk-classrooms/intent/:id";

export function BulkClassRoomIntentDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    intent,
    loading: loadingIntent,
    error: errorIntent,
  } = useGetBulkClassRoomCreationIntent(id ?? "");

  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false);
  const [substitutionResult, setSubstitutionResult] =
    useState<TeacherSubstitutionResultGraphqlDto | null>(null);

  const { try1on1TeacherSubstitution, loading: loadingSubstitution } =
    useTry1on1TeacherSubstitution();

  const { createBulk1on1ClassRooms, loading: loadingCreateClassRooms } =
    useCreateBulk1on1ClassRooms();

  const { deleteBulkClassRoomCreationIntent, loading: loadingDelete } =
    useDeleteBulkClassRoomCreationIntent();

  const { updateBulkClassRoomCreationIntent, loading: loadingUpdate } =
    useUpdateBulkClassRoomCreationIntent();

  const { franchisees, loading: loadingFranchisees } = useAllFranchisees({
    isOneOnOne: true,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editTeacherId, setEditTeacherId] = useState<string>("");
  const [editStartDate, setEditStartDate] = useState<Date>(new Date());
  const [editNumberOfWeeks, setEditNumberOfWeeks] = useState<number>(1);
  const [editMaxClassRoomNumber, setEditMaxClassRoomNumber] =
    useState<number>(1);
  const [editWeekSlots, setEditWeekSlots] = useState<
    Array<{ dayOfWeek: number; startTime: string; endTime: string }>
  >([]);

  const [localExcludedDates, setLocalExcludedDates] = useState<string[]>([]);
  const [localAdditionalSlots, setLocalAdditionalSlots] = useState<
    Array<{ startDateUtc: string; endDateUtc: string }>
  >([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlotStart, setNewSlotStart] = useState<Date>(new Date());

  const { show, showPopup, description, title } = useMessagePopupController(
    4 * 1000,
  );
  const askConfirmation = useConfirm();

  const handleTrySubstitution = async () => {
    if (!id) return;

    try {
      const result = await try1on1TeacherSubstitution(id, "Europe/Rome");
      setSubstitutionResult(result);
      setShowSubstitutionModal(true);
      // Ricarica gli slot per aggiornare la vista
      await refetchSlots();
    } catch (error) {
      const err = error as Error;
      showPopup("Errore", err.message ?? "Errore durante la sostituzione");
    }
  };

  const handleCreateClassRooms = async () => {
    if (!id) return;

    try {
      const result = await createBulk1on1ClassRooms(id);
      // Redirect alla schermata di dettaglio della richiesta
      navigate(`/bulk-classrooms/request/${result.requestId}`);
    } catch (error) {
      const err = error as Error;
      showPopup(
        "Errore",
        err.message ?? "Errore durante la creazione delle classi",
      );
    }
  };

  const handleDeleteIntent = async () => {
    if (!id) return;
    if (
      !(await askConfirmation({
        title: "Eliminare questo intent?",
        message: "L'operazione non è reversibile.",
        confirmLabel: "Elimina",
        isDestructive: true,
      }))
    )
      return;

    try {
      await deleteBulkClassRoomCreationIntent(id);
      navigate("/unredeemed-1on1-plans");
    } catch (error) {
      const err = error as Error;
      showPopup(
        "Errore",
        err.message ?? "Errore durante l'eliminazione dell'intent",
      );
    }
  };

  // Initialise edit state whenever intent data arrives
  useEffect(() => {
    if (!intent) return;
    setEditTeacherId(intent.teacher?.id ?? "");
    setEditStartDate(
      intent.startDate ? new Date(intent.startDate) : new Date(),
    );
    setEditNumberOfWeeks(intent.numberOfWeeks ?? 1);
    setEditMaxClassRoomNumber(intent.maxClassRoomNumber ?? 1);
    setEditWeekSlots(
      intent.weekSlots?.map((s) => ({
        dayOfWeek: s.dayOfWeek,
        startTime: s.startTime,
        endTime: s.endTime,
      })) ?? [],
    );
    setLocalExcludedDates(intent.excludedSlots ?? []);
    setLocalAdditionalSlots(
      (intent.additionalSlots ?? []).map(({ startDateUtc, endDateUtc }) => ({
        startDateUtc,
        endDateUtc,
      })),
    );
  }, [intent]);

  const handleUpdate = async () => {
    if (!id) return;
    try {
      await updateBulkClassRoomCreationIntent({
        id,
        teacherId: editTeacherId || undefined,
        startDate: editStartDate.toISOString(),
        numberOfWeeks: editNumberOfWeeks,
        maxClassRoomNumber: editMaxClassRoomNumber,
        weekSlots: editWeekSlots,
      });
      setIsEditing(false);
      await refetchSlots();
    } catch (error) {
      const err = error as Error;
      showPopup("Errore", err.message ?? "Errore durante l'aggiornamento");
    }
  };

  function getNextRoundHour(): Date {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    if (d.getHours() < 7) d.setHours(7);
    if (d.getHours() > 22) {
      d.setDate(d.getDate() + 1);
      d.setHours(7);
    }
    return d;
  }

  const isAdditionalSlot = useCallback(
    (slotStartUtc: string | Date) => {
      const ms = new Date(slotStartUtc as string).getTime();
      return localAdditionalSlots.some(
        (s) => new Date(s.startDateUtc).getTime() === ms,
      );
    },
    [localAdditionalSlots],
  );

  const handleRemoveSlot = async (slotStartUtc: string | Date) => {
    if (!id) return;
    try {
      let newExcluded = localExcludedDates;
      let newAdditional = localAdditionalSlots;

      if (isAdditionalSlot(slotStartUtc)) {
        const ms = new Date(slotStartUtc as string).getTime();
        newAdditional = localAdditionalSlots.filter(
          (s) => new Date(s.startDateUtc).getTime() !== ms,
        );
        setLocalAdditionalSlots(newAdditional);
      } else {
        const iso = new Date(slotStartUtc as string).toISOString();
        newExcluded = [...localExcludedDates, iso];
        setLocalExcludedDates(newExcluded);
      }

      await updateBulkClassRoomCreationIntent({
        id,
        excludedSlots: newExcluded,
        additionalSlots: newAdditional,
      });
      await refetchSlots();
    } catch (error) {
      const err = error as Error;
      showPopup(
        "Errore",
        err.message ?? "Errore durante la rimozione dello slot",
      );
    }
  };

  const handleAddSlot = async () => {
    if (!id) return;
    try {
      const endDate = new Date(newSlotStart.getTime() + 60 * 60 * 1000);
      const newAdditional = [
        ...localAdditionalSlots,
        {
          startDateUtc: newSlotStart.toISOString(),
          endDateUtc: endDate.toISOString(),
        },
      ];
      setLocalAdditionalSlots(newAdditional);
      await updateBulkClassRoomCreationIntent({
        id,
        excludedSlots: localExcludedDates,
        additionalSlots: newAdditional,
      });
      setShowAddSlot(false);
      await refetchSlots();
    } catch (error) {
      const err = error as Error;
      showPopup(
        "Errore",
        err.message ?? "Errore durante l'aggiunta dello slot",
      );
    }
  };

  const cleanedWeekSlots = useMemo(() => {
    if (!intent?.weekSlots) return [];
    return intent.weekSlots.map((slot) => ({
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
  }, [intent?.weekSlots]);

  const {
    slots,
    loading: loadingSlots,
    error: errorSlots,
    refetch: refetchSlots,
  } = useCalculateBulkClassRoomCreationSlots({
    teacherId: intent?.teacher?.id ?? "",
    studentId: intent?.user?.id ?? undefined,
    startDate: intent?.startDate ?? "",
    numberOfWeeks: intent?.numberOfWeeks ?? 0,
    maxClassRoomNumber: intent?.maxClassRoomNumber ?? undefined,
    weekSlots: cleanedWeekSlots,
    excludedSlots: localExcludedDates,
    additionalSlots: localAdditionalSlots,
  });

  const sortedSlots = useMemo(() => {
    return [...slots].sort((a, b) => {
      // Metti in alto quelli con conflitti (teacher o user)
      const aHasConflict = a.teacherConflictingClass || a.userConflictingClass;
      const bHasConflict = b.teacherConflictingClass || b.userConflictingClass;

      if (aHasConflict && !bHasConflict) return -1;
      if (!aHasConflict && bHasConflict) return 1;
      // Altrimenti ordina per data
      return dayjs(a.startDateUtc).diff(dayjs(b.startDateUtc));
    });
  }, [slots]);

  if (loadingIntent || loadingSlots) {
    return <FullPageLoading />;
  }

  if (errorIntent || errorSlots || !intent) {
    return (
      <Layout>
        <Card>
          <ErrorFeedback
            title={"Errore nel caricamento dell'intent"}
            message={
              errorIntent?.message ||
              errorSlots?.message ||
              "Impossibile caricare l'intent"
            }
          />
        </Card>
      </Layout>
    );
  }

  const hasConflicts = sortedSlots.some(
    (slot) => slot.teacherConflictingClass || slot.userConflictingClass,
  );

  return (
    <>
      <ErrorMessagePopup title={title} description={description} show={show} />
      <SubstitutionResultModal
        show={showSubstitutionModal}
        onClose={() => setShowSubstitutionModal(false)}
        result={substitutionResult}
      />
      <Layout>
        <Card>
          <div className="flex items-start justify-between">
            <ScreenTitleBlock
              title={"Dettagli Bulk ClassRoom Intent"}
              breadCrumbs={
                <BreadCrumbsWrapper>
                  <BreadCrumbsItem title={"Dettagli Intent"} />
                </BreadCrumbsWrapper>
              }
            />
            <div className="w-fit">
              <DangerButton
                label={loadingDelete ? "Eliminazione..." : "Elimina Intent"}
                onClick={handleDeleteIntent}
                disabled={loadingDelete}
                iconLeft={<TrashIcon className="w-4 h-4" />}
              />
            </div>
          </div>
        </Card>

        <div className={"mt-4"}>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Informazioni Intent</h2>
              <div className="w-fit">
                <OutlinedButton
                  label={isEditing ? "Annulla" : "Modifica"}
                  onClick={() => setIsEditing((v) => !v)}
                  iconLeft={
                    isEditing ? undefined : (
                      <PencilSquareIcon className="w-4 h-4" />
                    )
                  }
                />
              </div>
            </div>

            {!isEditing ? (
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">ID:</span> {intent.id}
                </div>
                <div>
                  <span className="font-medium">Insegnante:</span>{" "}
                  {intent.teacher
                    ? `${intent.teacher.givenName} ${intent.teacher.familyName}`
                    : "—"}
                </div>
                <div>
                  <span className="font-medium">Studente:</span>{" "}
                  {intent.user
                    ? `${intent.user.givenName} ${intent.user.familyName}`
                    : "—"}
                </div>
                <div>
                  <span className="font-medium">Data inizio:</span>{" "}
                  {intent.startDate
                    ? formatDateTime(intent.startDate, { locale: "it" })
                    : "—"}
                </div>
                <div>
                  <span className="font-medium">Numero settimane:</span>{" "}
                  {intent.numberOfWeeks}
                </div>
                {intent.maxClassRoomNumber && (
                  <div>
                    <span className="font-medium">Max classi:</span>{" "}
                    {intent.maxClassRoomNumber}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <InputWithLabel label="Insegnante">
                  {loadingFranchisees ? (
                    <LoadingBlock sizeClassName={"h-10 w-full"} />
                  ) : (
                    <Select<string>
                      searchable
                      options={franchisees.map((f) => ({
                        id: f.id,
                        name: `${f.givenName} ${f.familyName} (${f.email})`,
                      }))}
                      value={editTeacherId}
                      onChange={(v) => setEditTeacherId(v ?? "")}
                    />
                  )}
                </InputWithLabel>

                <InputWithLabel label="Data di inizio">
                  <DateTimeInput
                    hourFromTo={{ from: 7, to: 22 }}
                    value={editStartDate}
                    onChange={(v) => v && setEditStartDate(v)}
                  />
                </InputWithLabel>

                <InputWithLabel label="Numero di settimane">
                  <TextInput
                    type="number"
                    value={editNumberOfWeeks}
                    onChange={(e) =>
                      setEditNumberOfWeeks(Number(e.target.value))
                    }
                  />
                </InputWithLabel>

                <InputWithLabel label="Numero massimo di classi">
                  <TextInput
                    type="number"
                    value={editMaxClassRoomNumber}
                    onChange={(e) =>
                      setEditMaxClassRoomNumber(Number(e.target.value))
                    }
                  />
                </InputWithLabel>

                <InputWithLabel label="Slot settimanali">
                  <WeekSlotInput
                    value={editWeekSlots}
                    onChange={setEditWeekSlots}
                  />
                </InputWithLabel>

                <div className="flex justify-end gap-2">
                  <div className="w-fit">
                    <OutlinedButton
                      label="Annulla"
                      onClick={() => setIsEditing(false)}
                    />
                  </div>
                  <PrimaryButton
                    label={loadingUpdate ? "Salvataggio..." : "Salva modifiche"}
                    loading={loadingUpdate}
                    onClick={handleUpdate}
                  />
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className={"mt-4"}>
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                Slot Calcolati ({sortedSlots.length})
              </h2>
              <div className="flex gap-2">
                {hasConflicts && (
                  <PrimaryButton
                    label="Risoluzione Automatica Conflitti"
                    onClick={handleTrySubstitution}
                    loading={loadingSubstitution}
                  />
                )}
                {!hasConflicts && (
                  <PrimaryButton
                    label="Crea Classi"
                    onClick={handleCreateClassRooms}
                    loading={loadingCreateClassRooms}
                  />
                )}
              </div>
            </div>
            <div className="space-y-3">
              {sortedSlots.map((slot, index) => {
                const hasConflict =
                  slot.teacherConflictingClass || slot.userConflictingClass;
                const hasOneOnOneConflict =
                  !!slot.teacherConflictingClass?.is1on1 ||
                  !!slot.userConflictingClass?.is1on1;
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-md border ${
                      hasConflict
                        ? hasOneOnOneConflict
                          ? "bg-orange-50 border-orange-300"
                          : "bg-red-50 border-red-300"
                        : "bg-green-50 border-green-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium">
                          {formatDateTime(slot.startDateUtc, {
                            withWeekday: true,
                            locale: "it",
                          })}{" "}
                          - {dayjs(slot.endDateUtc).format("HH:mm")}
                        </div>

                        {slot.teacherConflictingClass && (
                          <div
                            className={`mt-2 text-sm ${
                              slot.teacherConflictingClass.is1on1
                                ? "text-orange-700"
                                : "text-red-700"
                            }`}
                          >
                            <div className="font-semibold mb-1">
                              {slot.teacherConflictingClass.is1on1
                                ? "⚠️ Conflitto 1to1: insegnante già occupato"
                                : slot.teacherConflictingClass.type ===
                                    ClassRoomType.OnboardingLive
                                  ? "⚠️ Conflitto: insegnante impegnato in una live onboarding (sostituibile con un altro conduttore)"
                                  : "⚠️ Conflitto: insegnante già occupato"}
                            </div>
                            <div className="space-y-1 ml-4">
                              <div>
                                <span className="font-medium">
                                  Data classe:
                                </span>{" "}
                                {formatDateTime(
                                  slot.teacherConflictingClass.startedAtUtc,
                                  { locale: "it" },
                                )}{" "}
                                -{" "}
                                {dayjs(
                                  slot.teacherConflictingClass.endedAtUtc,
                                ).format("HH:mm")}
                              </div>
                              <div>
                                <span className="font-medium">
                                  Partecipanti:
                                </span>{" "}
                                {
                                  slot.teacherConflictingClass.participants
                                    .length
                                }{" "}
                                / {slot.teacherConflictingClass.capacity}
                              </div>
                              <div>
                                <span className="font-medium">Gruppo:</span>{" "}
                                {slot.teacherConflictingClass.userGroup.name}
                              </div>
                              <div>
                                <span className="font-medium">Livello:</span>{" "}
                                {slot.teacherConflictingClass.speakingLevel ??
                                  "-"}
                              </div>
                            </div>
                          </div>
                        )}

                        {slot.userConflictingClass && (
                          <div
                            className={`mt-2 text-sm ${
                              slot.userConflictingClass.is1on1
                                ? "text-orange-700"
                                : "text-orange-700"
                            }`}
                          >
                            <div className="font-semibold mb-1">
                              {slot.userConflictingClass.is1on1
                                ? "⚠️ Conflitto 1to1: utente già occupato"
                                : "⚠️ Conflitto: utente già ha una classe"}
                            </div>
                            <div className="space-y-1 ml-4">
                              <div>
                                <span className="font-medium">
                                  Data classe:
                                </span>{" "}
                                {formatDateTime(
                                  slot.userConflictingClass.startedAtUtc,
                                  { locale: "it" },
                                )}{" "}
                                -{" "}
                                {dayjs(
                                  slot.userConflictingClass.endedAtUtc,
                                ).format("HH:mm")}
                              </div>
                              <div>
                                <span className="font-medium">
                                  Partecipanti:
                                </span>{" "}
                                {slot.userConflictingClass.participants.length}{" "}
                                / {slot.userConflictingClass.capacity}
                              </div>
                              <div>
                                <span className="font-medium">Gruppo:</span>{" "}
                                {slot.userConflictingClass.userGroup.name}
                              </div>
                              <div>
                                <span className="font-medium">Livello:</span>{" "}
                                {slot.userConflictingClass.speakingLevel}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {hasConflict ? (
                          <span
                            className={`px-3 py-1 text-white rounded-md text-sm font-medium ${
                              hasOneOnOneConflict
                                ? "bg-orange-600"
                                : "bg-red-600"
                            }`}
                          >
                            {hasOneOnOneConflict
                              ? "Conflitto 1to1"
                              : "Conflitto"}
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-green-600 text-white rounded-md text-sm font-medium">
                            Disponibile
                          </span>
                        )}
                        <button
                          onClick={() => handleRemoveSlot(slot.startDateUtc)}
                          className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Rimuovi slot"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 border-t pt-4">
              <div className="w-fit">
                <OutlinedButton
                  label="Aggiungi slot"
                  onClick={() => {
                    setNewSlotStart(getNextRoundHour());
                    setShowAddSlot(true);
                  }}
                  iconLeft={<PlusIcon className="w-4 h-4" />}
                />
              </div>
            </div>
          </Card>
        </div>
      </Layout>

      {showAddSlot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowAddSlot(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-base font-semibold">
              Aggiungi slot (durata 1 ora)
            </h3>
            <DateTimeInput
              hourFromTo={{ from: 7, to: 22 }}
              value={newSlotStart}
              onChange={(v) => v && setNewSlotStart(v)}
            />
            <div className="flex gap-2 justify-end">
              <div className="w-fit">
                <OutlinedButton
                  label="Annulla"
                  onClick={() => setShowAddSlot(false)}
                />
              </div>
              <div className="w-fit">
                <PrimaryButton
                  label={loadingUpdate ? "Aggiunta..." : "Aggiungi"}
                  loading={loadingUpdate}
                  onClick={handleAddSlot}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
